import { Link } from 'react-router';
import { APP_NAME, SUPPORT_EMAIL } from '@/lib/constants';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="text-primary font-bold text-xl">{APP_NAME}</Link>
          <Link to="/" className="text-sm text-text-secondary hover:text-text transition-colors">Back to Home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <p className="text-sm text-text-secondary">
            <strong>Note:</strong> This privacy policy is a template and must be reviewed by a qualified legal professional before the platform goes live. It does not constitute legal advice.
          </p>
        </div>

        <p className="text-sm text-text-secondary">Last updated: July 2026</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-text mt-2">Privacy Policy</h1>

        <section className="mt-8 space-y-8 prose prose-gray max-w-none">
          <div>
            <h2 className="text-xl font-semibold">1. Information We Collect</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              When you use {APP_NAME}, we collect the following types of information:
            </p>
            <ul className="mt-2 space-y-1 text-text-secondary">
              <li><strong>Personal Information:</strong> Full name, date of birth, email address, phone number, state, city, parent or guardian name, and alternate phone number.</li>
              <li><strong>Academic Information:</strong> Educational qualifications, board or university, institution name, stream, passing year, percentage or CGPA, entrance exam details, and backlog status.</li>
              <li><strong>Preference Information:</strong> Course interests, preferred locations, budget range, accommodation requirements, and study mode preferences.</li>
              <li><strong>Documents:</strong> Student photograph, marksheets, and entrance exam scorecards uploaded for admission guidance.</li>
              <li><strong>Communication Data:</strong> Records of your interactions with our counsellors and any messages sent through our platform.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold">2. Documents Collected</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We collect the following documents necessary for college admission guidance:
            </p>
            <ul className="mt-2 space-y-1 text-text-secondary">
              <li>Student photograph</li>
              <li>Class 10 marksheet</li>
              <li>Class 12 or latest qualification marksheet</li>
              <li>Entrance exam scorecard (where applicable)</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold">3. Purpose of Collection</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We collect and process your information for the following purposes:
            </p>
            <ul className="mt-2 space-y-1 text-text-secondary">
              <li>Assessing your eligibility for various college programmes</li>
              <li>Providing personalised college recommendations</li>
              <li>Connecting you with an appropriate admission counsellor</li>
              <li>Communicating with you about suitable admission options</li>
              <li>Improving our counselling and guidance services</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold">4. OTP Verification</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We verify your phone number using a one-time password (OTP) sent via SMS. The OTP is used solely for verification purposes and is not stored in plain text in our systems. We work with third-party SMS providers to deliver OTP messages.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">5. Communication</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              By submitting an application, you consent to being contacted by our admission counsellors via phone, WhatsApp, or email regarding college admission options. You may also separately opt in to receive promotional communications. You can withdraw your consent at any time by contacting us.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">6. File Storage</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              Uploaded documents are stored securely on Cloudflare R2, a private cloud storage service. Documents are never publicly accessible. Files are renamed using random identifiers, and original filenames are stored separately for reference only.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">7. Access Controls</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              Access to your personal information and documents is restricted to authorised personnel on a need-to-know basis. Our admin team members have role-based access, and all document access is logged for audit purposes.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">8. Data Retention</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We retain your application data and documents for as long as necessary to provide our services. Active applications are retained during the counselling period. You may request deletion of your data at any time through our Data Deletion Request page.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">9. Data Deletion</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              You have the right to request deletion of your personal data. To do so, visit our{' '}
              <Link to="/data-deletion" className="text-primary hover:text-primary-dark underline">Data Deletion Request</Link> page.
              We will process your request within a reasonable timeframe after verifying your identity through OTP confirmation.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">10. Third-Party Service Providers</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We use the following third-party services:
            </p>
            <ul className="mt-2 space-y-1 text-text-secondary">
              <li><strong>Cloudflare:</strong> Hosting, CDN, D1 database, R2 storage, and Turnstile bot protection.</li>
              <li><strong>SMS Provider:</strong> For OTP delivery (MSG91 or similar service).</li>
              <li><strong>Analytics:</strong> Basic usage analytics (no personal data shared).</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold">11. Cookies</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We use essential cookies for session management and security. Our admin dashboard uses HTTP-only cookies for authentication. We do not use tracking cookies for advertising purposes.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">12. Security</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal data, including encryption in transit (TLS), secure cookie attributes, input validation, rate limiting, and access controls. However, no method of electronic storage is 100% secure.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">13. Your Rights</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              Subject to applicable laws, you have the right to access, correct, delete, or port your personal data. You also have the right to withdraw consent and to lodge a complaint with a supervisory authority.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">14. Contact Us</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              For privacy-related inquiries, contact us at{' '}
              <span className="font-medium">{SUPPORT_EMAIL}</span> or visit our{' '}
              <Link to="/contact" className="text-primary hover:text-primary-dark underline">Contact page</Link>.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-3xl px-4 py-6 text-center text-xs text-text-secondary">
          <p>&copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
